import { useState, useRef, useEffect } from "react";
import ButtonIconH from "../../components/ButtonIconH";

export default function WacomSignature(props) {
  const canvasRef = useRef(null);
  const stateRef = useRef({});
  const [status, setStatus] = useState("idle"); // idle | connecting | connected | error
  const [imagenBase64, setimagenBase64] = useState(null);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.save();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.restore();
  };

  const drawButtons = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width,
      H = canvas.height;
    const y = Math.floor((H * 6) / 7),
      h = H - y;
    const w = Math.floor(W / 3);
    ctx.font = "20px Arial";
    ctx.lineWidth = 1;
    ctx.strokeStyle = "black";
    ctx.beginPath();
    [
      ["OK", 0],
      ["Limpiar", w],
      ["Cancelar", w * 2],
    ].forEach(([text, x]) => {
      ctx.fillStyle = "#e5e7eb";
      ctx.fillRect(x, y, w, h);
      ctx.fillStyle = "black";
      ctx.rect(x, y, w, h);
      ctx.fillText(text, x + (w - ctx.measureText(text).width) / 2, y + 28);
    });
    ctx.stroke();
    ctx.closePath();
  };

  const processPoint = (point) => {
    const s = stateRef.current;
    const canvas = canvasRef.current;
    if (!canvas || !s.cap || !s.inkThreshold) return;
    const ctx = canvas.getContext("2d");
    const W = canvas.width,
      H = canvas.height;
    const nx = Math.round((W * point.x) / s.cap.tabletMaxX);
    const ny = Math.round((H * point.y) / s.cap.tabletMaxY);
    const isDown2 = s.isDown
      ? point.pressure > s.inkThreshold.offPressureMark
      : point.pressure > s.inkThreshold.onPressureMark;

    const btnY = Math.floor((H * 6) / 7);
    const btnW = Math.floor(W / 3);
    const inButtonArea = ny >= btnY;

    // Detectar toque en zona de botones (pen-down → pen-up)
    if (!s.isDown && isDown2 && inButtonArea) {
      s.btnPressed = true;
    }
    if (s.isDown && !isDown2 && s.btnPressed && inButtonArea) {
      s.btnPressed = false;
      if (nx < btnW) handleOk();
      else if (nx < btnW * 2) handleClear();
      else disconnect();
      s.isDown = isDown2;
      return;
    }
    if (s.isDown && !isDown2) s.btnPressed = false;

    // Dibujar solo fuera de la zona de botones
    if (!inButtonArea) {
      if (!s.isDown && isDown2) s.lastPoint = { x: nx, y: ny };
      const dist =
        Math.pow(nx - s.lastPoint.x, 2) + Math.pow(ny - s.lastPoint.y, 2);
      if ((isDown2 && dist > 10) || (s.isDown && !isDown2)) {
        ctx.beginPath();
        ctx.lineWidth = 1.5;
        ctx.strokeStyle = "#111";
        ctx.lineCap = "round";
        ctx.moveTo(s.lastPoint.x, s.lastPoint.y);
        ctx.lineTo(nx, ny);
        ctx.stroke();
        ctx.closePath();
        s.lastPoint = { x: nx, y: ny };
      }
    }
    s.isDown = isDown2;
  };

  const connect = async () => {
    setStatus("connecting");
    const s = stateRef.current;
    s.penData = [];
    s.isDown = false;
    s.lastPoint = { x: 0, y: 0 };

    try {
      const STU = window.WacomGSS.STU;
      const p = new STU.Protocol();

      const devices = await STU.getUsbDevices();
      if (!devices.length) throw new Error("No se encontró tableta STU");

      const intf = new STU.UsbInterface();
      await intf.Constructor();
      await intf.connect(devices[0], true);
      s.intf = intf;

      const tablet = new STU.Tablet();
      await tablet.Constructor(intf);
      s.tablet = tablet;

      [s.inkThreshold, s.cap] = await Promise.all([
        tablet.getInkThreshold(),
        tablet.getCapability(),
      ]);

      const pid = await tablet.getProductId();
      const encFlag = await STU.ProtocolHelper.simulateEncodingFlag(
        pid,
        s.cap.encodingFlag,
      );
      const supWrite = await tablet.supportsWrite();
      s.encodingMode =
        encFlag & p.EncodingFlag.EncodingFlag_24bit
          ? supWrite
            ? p.EncodingMode.EncodingMode_24bit_Bulk
            : p.EncodingMode.EncodingMode_24bit
          : encFlag & p.EncodingFlag.EncodingFlag_16bit
            ? supWrite
              ? p.EncodingMode.EncodingMode_16bit_Bulk
              : p.EncodingMode.EncodingMode_16bit
            : p.EncodingMode.EncodingMode_1bit;

      await tablet.setClearScreen();
      clearCanvas();
      drawButtons();

      // Limitar zona de tinta a la parte de firma (excluir botones)
      const btnY = Math.floor((s.cap.screenHeight * 6) / 7);
      await tablet.setHandwritingDisplayArea({
        upperLeftXpixel: 0,
        upperLeftYpixel: 0,
        lowerRightXpixel: s.cap.screenWidth,
        lowerRightYpixel: btnY,
      });

      const imgData = await STU.ProtocolHelper.resizeAndFlatten(
        canvasRef.current.toDataURL("image/jpeg"),
        0,
        0,
        0,
        0,
        s.cap.screenWidth,
        s.cap.screenHeight,
        s.encodingMode,
        1,
        false,
        0,
        true,
      );
      s.imgData = imgData;
      await tablet.writeImage(s.encodingMode, imgData);
      await tablet.setInkingMode(p.InkingMode.InkingMode_On);

      const rh = new STU.ProtocolHelper.ReportHandler();
      const onPen = (report) => {
        processPoint(report);
        s.penData.push(report);
      };
      rh.onReportPenData = onPen;
      rh.onReportPenDataOption = onPen;
      rh.onReportPenDataTimeCountSequence = onPen;
      await rh.startReporting(tablet, true);

      setStatus("connected");
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const disconnect = async () => {
    const s = stateRef.current;
    try {
      if (s.tablet) {
        const p = new window.WacomGSS.STU.Protocol();
        await s.tablet.setInkingMode(p.InkingMode.InkingMode_Off);
        await s.tablet.endCapture?.();
        if (s.imgData) {
          await s.imgData.remove?.();
          s.imgData = null;
        }
        await s.tablet.setClearScreen();
        await s.tablet.disconnect();
        s.tablet = null;
        s.intf = null;
      }
    } catch (_) {}
    setStatus("idle");
  };

  const handleClear = async () => {
    const s = stateRef.current;
    s.penData = [];
    s.isDown = false;
    clearCanvas();
    drawButtons();
    if (s.tablet) await s.tablet.writeImage(s.encodingMode, s.imgData);
  };

  const handleOk = () => {
    const canvas = canvasRef.current;
    const btnY = Math.floor((canvas.height * 6) / 7);
    // Exportar solo la zona de firma (sin botones)
    const exportCanvas = document.createElement("canvas");
    exportCanvas.width = canvas.width;
    exportCanvas.height = btnY;
    exportCanvas
      .getContext("2d")
      .drawImage(canvas, 0, 0, canvas.width, btnY, 0, 0, canvas.width, btnY);
    const base64 = exportCanvas.toDataURL("image/png");
    setimagenBase64(base64);
    // console.log("Firma base64:", base64);
    disconnect();
  };

  useEffect(() => {
    clearCanvas();
    return () => {
      disconnect();
    };
  }, []);

  if (!props.show) {
    return <></>;
  }

  return (
    <>
      <div className="patient-data">
        <div className="patient-data-header">
          <span className="header-text">Firma WACOM</span>
          <div className="patient-header-buttons"></div>
        </div>
        <div className="patient-data-content">
          <div className="action-bar">
            <div className="action-bar-resume">
              <span>Debug</span>
              <span>|</span>
              <span className="signed-text">Firma WACOM</span>
            </div>
            <button
              className="action-bar-return"
              onClick={() => {
                props.onGoBack();
              }}
            >
              <span>Volver</span>
              <img
                src="/images/return.svg"
                loading="lazy"
                alt="Icon"
                className="return-icon"
              />
            </button>
          </div>
          <div className="patient-sections">
            <div className="patient-section">
              <div className="section-content">
                <div className="section-todo-tests">
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "row",
                      gap: 16,
                      alignItems: "flex-start",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                      }}
                    >
                      <canvas
                        ref={canvasRef}
                        width={560}
                        height={336}
                        style={{
                          border: "1px solid #ccc",
                          background: "#fff",
                          display: "block",
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "row",
                          gap: 8,
                        }}
                      >
                        {status !== "connected" ? (
                          <ButtonIconH
                            text={
                              status === "connecting"
                                ? "Conectando..."
                                : "Conectar"
                            }
                            onClick={connect}
                            disabled={status === "connecting"}
                          />
                        ) : (
                          <>
                            <ButtonIconH
                              text="OK — Guardar"
                              onClick={handleOk}
                              highlight
                            />
                            <ButtonIconH text="Limpiar" onClick={handleClear} />
                            <ButtonIconH text="Cancelar" onClick={disconnect} />
                          </>
                        )}
                      </div>
                      {status === "error" && (
                        <p style={{ color: "red" }}>
                          Error al conectar — revisa el log de consola (F12)
                        </p>
                      )}
                    </div>
                    {imagenBase64 && (
                      <img
                        src={imagenBase64}
                        alt="Firma"
                        style={{
                          border: "1px solid #ccc",
                          background: "#fff",
                          maxWidth: 280,
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
